import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { contractsApi, type ContractGrupa, type ContractOverview as Pregled, type ContractRedUgovora } from "../../api/contracts";
import { KPI } from "../../components/primitives.jsx";
import { Select } from "../../components/Select";
import { formatDate, formatMoney, formatNumber, intlLocale } from "../../lib/format";

/**
 * Šta je prodaja ugovorila za godinu i koliko je od toga isporučeno.
 *
 * Spisak ugovora ispod odgovara na administrativno pitanje — ko je potpisao i do kada. Ovo
 * odgovara na komercijalno: koliko smo vezali, u čemu, kod koga, i koliko je stvarno otišlo.
 * Bez toga se ugovor čita kao dokument, a ne kao obaveza koja se puni.
 */
export function ContractOverview() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const [godina, setGodina] = useState<number>(new Date().getFullYear());
  const [podaci, setPodaci] = useState<Pregled | null>(null);
  const [greska, setGreska] = useState(false);

  useEffect(() => {
    let otkazano = false;
    setPodaci(null);
    setGreska(false);
    contractsApi.overview(godina, i18n.resolvedLanguage)
      .then((p) => { if (!otkazano) setPodaci(p); })
      .catch(() => { if (!otkazano) setGreska(true); });
    return () => { otkazano = true; };
  }, [godina, i18n.resolvedLanguage]);

  // Spisak godina kreće od 2025, kad je uvedena serija ugovora koja se danas koristi.
  const godine = useMemo(() => {
    const sada = new Date().getFullYear();
    return Array.from({ length: sada - 2024 }, (_, i) => sada - i);
  }, []);

  const eur = (n: number | null) =>
    n == null ? "—" : formatMoney(n, "EUR", locale, { maximumFractionDigits: 0 });
  const kg = (n: number | null) =>
    n == null ? "—" : `${formatNumber(n, locale, { maximumFractionDigits: 0 })} kg`;
  const pct = (n: number | null) =>
    n == null ? "—" : `${formatNumber(n, locale, { maximumFractionDigits: 1 })}%`;

  return (
    <section style={okvir}>
      <header style={zaglavlje}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>
            {t("contracts.overview.title", "Ugovoreno i realizovano")}
          </h2>
          <div style={tiho}>
            {t("contracts.overview.subtitle",
               "Potpisani ugovori koji važe u izabranoj godini, svaki za ceo rok. Fakturisano je ono što je tom kupcu za taj artikal fakturisano u roku ugovora, posle rabata i bez PDV-a, kao i ugovoreno; kad istu robu pokriva više ugovora, stariji se puni prvi.")}
          </div>
        </div>
        <Select
          value={String(godina)}
          ariaLabel={t("contracts.overview.year", "Godina")}
          options={godine.map((g) => ({ value: String(g), label: String(g) }))}
          onChange={(g) => setGodina(Number(g))}
          style={{ minWidth: 110 }}
        />
      </header>

      {greska ? (
        <div className="empty">{t("contracts.overview.failed", "Pregled nije mogao da se učita.")}</div>
      ) : !podaci ? (
        <div style={tiho}>{t("common.loading", "Učitavanje…")}</div>
      ) : podaci.ukupno.ugovora === 0 ? (
        <div className="empty">
          {t("contracts.overview.empty", "Za ovu godinu nema potpisanih ugovora.")}
        </div>
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KPI label={t("contracts.overview.contracted", "Ugovoreno")}
                 value={eur(podaci.ukupno.ugovorenaVrednost)}
                 delta={undefined} trend={undefined} icon={undefined} />
            <KPI label={t("contracts.overview.invoiced", "Fakturisano")}
                 value={eur(podaci.ukupno.isporucenaVrednost)}
                 delta={undefined} trend={undefined} icon={undefined} />
            {/* Preostalo je ono zbog čega se ovaj ekran i otvara: obaveza koja još stoji. */}
            <KPI label={t("contracts.overview.remaining", "Preostalo")}
                 value={eur(podaci.ukupno.preostalaVrednost)}
                 delta={undefined} trend={undefined} icon={undefined} />
            <KPI label={t("contracts.overview.fulfilment", "Realizacija")}
                 value={pct(podaci.ukupno.realizacijaPct)}
                 delta={undefined} trend={undefined} icon={undefined} />
          </div>

          {/* Realizacija bez proteklog roka nema meru: 40% u januaru i 40% u decembru nisu ista
              vest. Zato stoje jedno uz drugo, sa razlikom izrečenom rečima. */}
          <Tempo realizacija={podaci.ukupno.realizacijaPct} proteklo={podaci.ukupno.protekloPct}
                 locale={locale} t={t} />

          <div style={tiho}>
            {t("contracts.overview.counts", "{{ugovora}} ugovora · {{kupaca}} kupaca · {{artikala}} artikala",
               { ugovora: podaci.ukupno.ugovora, kupaca: podaci.ukupno.kupaca, artikala: podaci.ukupno.artikala })}
            {" · "}
            {t("contracts.overview.remainingQty", "preostalo {{kg}}", { kg: kg(podaci.ukupno.preostalaKolicina) })}
            {podaci.ukupno.ugovorenaVrednostPrethodna != null && (
              <>
                {" · "}
                {t("contracts.overview.previousYear", "prethodna godina {{iznos}}",
                   { iznos: eur(podaci.ukupno.ugovorenaVrednostPrethodna) })}
              </>
            )}
          </div>

          <IsticuUskoro redovi={podaci.poUgovoru} locale={locale} t={t} />

          {/* Sve tri podele jedna ispod druge, pune širine.
              Tri uske kolone jedna pored druge sekle su imena („Soji...", „Kerr..."), a prekidač
              sa tri taba krio bi dve trećine podataka — a ovo je pregled, ne pretraga. Svaka
              tabela ima svoju pretragu, sortiranje i filter, jer se za svaku pita drugo. */}
          <Odeljak naslov={t("contracts.overview.byLine", "Po liniji proizvoda")}
                   redovi={podaci.poLiniji} eur={eur} kg={kg} locale={locale} t={t} />
          <Odeljak naslov={t("contracts.overview.byProduct", "Po artiklu")}
                   redovi={podaci.poArtiklu} eur={eur} kg={kg} locale={locale} t={t} />
          <Odeljak naslov={t("contracts.overview.byCustomer", "Po kupcu")}
                   redovi={podaci.poKupcu} eur={eur} kg={kg} locale={locale} t={t} />
        </>
      )}
    </section>
  );
}

/**
 * Ugovori kojima ističe rok.
 *
 * Broj u rečenici („5 ističe u 30 dana") kaže da nešto treba uraditi, ali ne i šta: ne vidi se ni
 * koji ugovor, ni koji kupac, ni koliko je ostalo neisporučeno. Ovo je jedina stvar na ekranu o
 * kojoj treba odlučiti danas, pa stoji kao odeljak, odmah ispod zbira.
 *
 * Granica je devedeset dana, a ne trideset: ugovor koji ističe za mesec dana je već zakasnio za
 * razgovor o produženju. Istekli se takođe prikazuju, dok im stoji neisporučena količina — rok je
 * prošao, a obaveza nije.
 */
function IsticuUskoro({
  redovi, locale, t,
}: {
  redovi: ContractRedUgovora[];
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  const [granica, setGranica] = useState(90);

  const spisak = useMemo(() => redovi
    .filter((r) => r.danaDoIsteka != null && r.danaDoIsteka <= granica)
    .filter((r) => {
      // Istekao ugovor je zanimljiv samo ako je nešto ostalo neisporučeno.
      if ((r.danaDoIsteka ?? 0) >= 0) return true;
      const preostalo = (r.ugovorenaVrednost ?? 0) - (r.isporucenaVrednost ?? 0);
      return preostalo > 0;
    })
    .sort((a, b) => (a.danaDoIsteka ?? 0) - (b.danaDoIsteka ?? 0)),
    [redovi, granica]);

  if (redovi.length === 0) return null;

  return (
    <section style={{ ...okvir, borderColor: spisak.length > 0 ? "var(--warn)" : "var(--border)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14 }}>{t("contracts.overview.expiring.title", "Ističe rok")}</h3>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--ink-3)" }}>
            {t("contracts.overview.expiring.lead",
               "Ugovori kojima rok ističe, i istekli kojima je ostao deo ugovorenog.")}
          </p>
        </div>
        <span style={{ display: "flex", gap: 6 }}>
          {[30, 90, 180].map((d) => (
            <button key={d} type="button" onClick={() => setGranica(d)} style={cip(granica === d)}>
              {t("contracts.overview.expiring.within", "{{count}} dana", { count: d })}
            </button>
          ))}
        </span>
      </div>

      {spisak.length === 0 ? (
        <p style={{ ...tiho, margin: 0 }}>
          {t("contracts.overview.expiring.none", "Nijedan ugovor ne ističe u tom roku.")}
        </p>
      ) : (
        <table className="tbl" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>{t("contracts.overview.expiring.contract", "Ugovor")}</th>
              <th>{t("contracts.overview.expiring.customer", "Kupac")}</th>
              <th style={desno}>{t("contracts.overview.colRemaining", "Preostalo")}</th>
              <th style={desno}>{t("contracts.overview.fulfilment", "Realizacija")}</th>
              <th style={desno}>{t("contracts.overview.expiring.deadline", "Rok")}</th>
            </tr>
          </thead>
          <tbody>
            {spisak.map((r) => {
              const isteklo = (r.danaDoIsteka ?? 0) < 0;
              const preostaloEur = (r.ugovorenaVrednost ?? 0) - (r.isporucenaVrednost ?? 0);
              const preostaloKg = (r.ugovorenaKolicina ?? 0) - (r.isporucenaKolicina ?? 0);
              return (
                <tr key={r.ugovorId}>
                  <td>
                    <a href={`/admin/contracts?open=${r.ugovorId}`} style={{ color: "var(--brand-ink)", textDecoration: "none" }}>{r.broj ?? "—"}</a>
                    <div style={sitno}>{r.naslov ?? ""}</div>
                  </td>
                  <td>{r.kupac ?? "—"}</td>
                  <td style={desno}>
                    <div>{formatMoney(Math.max(0, preostaloEur), "EUR", locale, { maximumFractionDigits: 0 })}</div>
                    {/* A contract without kilogram lines has no kilograms, so none are left either. */}
                    {r.ugovorenaKolicina != null && (
                      <div style={sitno}>
                        {formatNumber(Math.max(0, preostaloKg), locale, { maximumFractionDigits: 0 })} kg
                      </div>
                    )}
                  </td>
                  <td style={desno}>
                    {r.realizacijaPct == null
                      ? "—"
                      : `${formatNumber(r.realizacijaPct, locale, { maximumFractionDigits: 0 })}%`}
                    {/* Contracts for the same goods share what was invoiced, the older one first; the
                        figure is this contract's share, and the note says it is shared. */}
                    {r.deliSa > 1 && (
                      <div style={sitno}>{t("contracts.overview.shared", "dele {{count}} ugovora", { count: r.deliSa })}</div>
                    )}
                  </td>
                  <td style={{ ...desno, color: isteklo ? "var(--err)" : "var(--warn)", fontWeight: 600 }}>
                    {isteklo
                      ? t("contracts.overview.expiring.expiredFor", "isteklo pre {{count}} dana", { count: Math.abs(r.danaDoIsteka ?? 0) })
                      : t("contracts.overview.expiring.inDays", "za {{count}} dana", { count: r.danaDoIsteka ?? 0 })}
                    <div style={sitno}>{r.istice ? formatDate(r.istice) : ""}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Realizacija naspram proteklog roka.
 *
 * Sam procenat realizacije ne kaže ništa dok se ne zna koliko je vremena prošlo. Zato se obe
 * veličine prikazuju jedna ispod druge, na istoj skali, a razlika se izriče rečima — jer je ona
 * ono što se traži, a ne dva broja koja treba oduzeti u glavi.
 */
function Tempo({
  realizacija, proteklo, locale, t,
}: {
  realizacija: number | null;
  proteklo: number | null;
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  if (realizacija == null || proteklo == null) return null;
  const razlika = realizacija - proteklo;
  // Pet poena je prag ispod kog razlika nije vest nego šum u zaokruživanju.
  const zaostaje = razlika < -5;
  const ispred = razlika > 5;
  const boja = zaostaje ? "var(--err)" : ispred ? "var(--ok)" : "var(--warn)";
  return (
    <div style={tempoOkvir}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <Linija naslov={t("contracts.overview.fulfilment", "Realizacija")} vrednost={realizacija}
                boja={boja} locale={locale} />
        <Linija naslov={t("contracts.overview.elapsed", "Proteklo roka")} vrednost={proteklo}
                boja="var(--ink-4)" locale={locale} />
      </div>
      <div style={{ ...tiho, color: boja, fontWeight: 600, whiteSpace: "nowrap" }}>
        {zaostaje
          ? t("contracts.overview.behind", "zaostaje {{n}} poena", {
              n: formatNumber(Math.abs(razlika), locale, { maximumFractionDigits: 0 }) })
          : ispred
            ? t("contracts.overview.ahead", "ispred roka {{n}} poena", {
                n: formatNumber(razlika, locale, { maximumFractionDigits: 0 }) })
            : t("contracts.overview.onTrack", "u skladu sa rokom")}
      </div>
    </div>
  );
}

function Linija({ naslov, vrednost, boja, locale }:
  { naslov: string; vrednost: number; boja: string; locale: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <span style={{ ...tiho, width: 110, flexShrink: 0 }}>{naslov}</span>
      <span style={{ ...traka, flex: 1, height: 6 }}>
        <span style={{ ...trakaPuna, width: `${Math.min(100, Math.max(0, vrednost))}%`, background: boja, display: "block" }} />
      </span>
      <span style={{ ...tiho, width: 46, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {formatNumber(vrednost, locale, { maximumFractionDigits: 0 })}%
      </span>
    </div>
  );
}

/** Naslov i tabela; naslov stoji izvan okvira da se tri odeljka razlikuju na prvi pogled. */
function Odeljak({ naslov, ...ostalo }: { naslov: string } & React.ComponentProps<typeof Tabela>) {
  return (
    <div>
      <h3 style={podnaslov}>{naslov}</h3>
      <Tabela {...ostalo} />
    </div>
  );
}

/**
 * Jedno razvrstavanje, sa pretragom, sortiranjem i filterom po tempu.
 *
 * Spisak od pedeset artikala ili dvadeset osam kupaca ne čita se odozgo nadole. Čovek dolazi sa
 * pitanjem — „ko najviše zaostaje", „gde je najviše ostalo", „šta je sa Cefetrom" — i tabela mora
 * da odgovori u jednom potezu. Zato se sortira klikom na zaglavlje, traži po imenu, i sužava na
 * one koji zaostaju.
 *
 * Sve ide u pregledaču, jer su skupovi mali i jedan upit ih već donosi; odlazak na server za
 * svako sortiranje bio bi sporiji od samog sortiranja.
 */
type Kolona = "naziv" | "ugovoreno" | "isporuceno" | "preostalo" | "realizacija";

function Tabela({
  redovi, eur, kg, locale, t,
}: {
  redovi: ContractGrupa[];
  eur: (n: number | null) => string;
  kg: (n: number | null) => string;
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  const [q, setQ] = useState("");
  const [kolona, setKolona] = useState<Kolona>("ugovoreno");
  const [opadajuce, setOpadajuce] = useState(true);
  const [tempo, setTempo] = useState<"sve" | "zaostaje" | "uskladu" | "ispred">("sve");

  // Zaostajanje se meri protiv proteklog roka, isto kao i boja trake; pet poena je prag ispod kog
  // je razlika zaokruživanje, a ne vest.
  const razlika = (r: ContractGrupa) =>
    r.realizacijaPct == null || r.protekloPct == null ? null : r.realizacijaPct - r.protekloPct;

  const prikazani = useMemo(() => {
    const tekst = q.trim().toLowerCase();
    const f = redovi.filter((r) => {
      if (tekst && !r.naziv.toLowerCase().includes(tekst)) return false;
      if (tempo === "sve") return true;
      const d = razlika(r);
      if (d == null) return false;
      return tempo === "zaostaje" ? d < -5 : tempo === "ispred" ? d > 5 : d >= -5 && d <= 5;
    });
    const broj = (r: ContractGrupa) =>
      kolona === "ugovoreno" ? r.ugovorenaVrednost
      : kolona === "isporuceno" ? r.isporucenaVrednost
      : kolona === "preostalo" ? r.preostalaVrednost
      : r.realizacijaPct;
    return [...f].sort((a, b) => {
      if (kolona === "naziv") {
        const c = a.naziv.localeCompare(b.naziv, locale);
        return opadajuce ? -c : c;
      }
      // Prazno uvek na dnu, bez obzira na smer: red bez broja nije ni najveći ni najmanji.
      const x = broj(a); const y = broj(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return opadajuce ? y - x : x - y;
    });
  }, [redovi, q, kolona, opadajuce, tempo, locale]);

  const sortiraj = (k: Kolona) => {
    if (k === kolona) setOpadajuce((o) => !o);
    else { setKolona(k); setOpadajuce(k !== "naziv"); }
  };
  const strelica = (k: Kolona) => (kolona === k ? (opadajuce ? " ↓" : " ↑") : "");
  const zaglavljeKol = (k: Kolona, naslov: string, sirina?: string) => (
    <th style={{ ...(k === "naziv" ? {} : desno), width: sirina, cursor: "pointer", userSelect: "none" }}
        onClick={() => sortiraj(k)}
        aria-sort={kolona === k ? (opadajuce ? "descending" : "ascending") : "none"}>
      {naslov}{strelica(k)}
    </th>
  );

  return (
    <div style={kutija}>
      <div style={alatke}>
        <input
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("contracts.overview.search", "Pretraga po nazivu…")}
          aria-label={t("contracts.overview.search", "Pretraga po nazivu…")}
          style={{ flex: 1, minWidth: 180, maxWidth: 320 }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {([
            ["sve", t("contracts.overview.filterAll", "Sve")],
            ["zaostaje", t("contracts.overview.filterBehind", "Zaostaju")],
            ["uskladu", t("contracts.overview.filterOnTrack", "U skladu")],
            ["ispred", t("contracts.overview.filterAhead", "Ispred")],
          ] as const).map(([k, naziv]) => (
            <button key={k} type="button" onClick={() => setTempo(k)} style={cip(tempo === k)}>
              {naziv}
            </button>
          ))}
        </div>
        <span style={{ ...tiho, marginLeft: "auto", whiteSpace: "nowrap" }}>
          {t("contracts.overview.shown", "{{n}} od {{ukupno}}")
            .replace("{{n}}", String(prikazani.length))
            .replace("{{ukupno}}", String(redovi.length))}
        </span>
      </div>

      {prikazani.length === 0 ? (
        <div style={tiho}>{t("contracts.overview.noRows", "Nema redova.")}</div>
      ) : (
        <table className="tbl" style={{ width: "100%" }}>
          <thead>
            <tr>
              {zaglavljeKol("naziv", t("contracts.overview.colName", "Naziv"), "34%")}
              {zaglavljeKol("ugovoreno", t("contracts.overview.colContracted", "Ugovoreno"))}
              {zaglavljeKol("isporuceno", t("contracts.overview.colInvoiced", "Fakturisano"))}
              {zaglavljeKol("preostalo", t("contracts.overview.colRemaining", "Preostalo"))}
              {zaglavljeKol("realizacija", t("contracts.overview.colFulfilment", "Realizacija"), "140px")}
            </tr>
          </thead>
          <tbody>
            {prikazani.map((r) => (
              <tr key={r.kljuc}>
                <td title={r.naziv} style={naziv}>
                  {r.naziv}
                  {r.ugovora > 1 && (
                    <span style={sitno}>
                      {" "}{t("contracts.overview.contractsCount", "· {{n}} ugovora")
                        .replace("{{n}}", String(r.ugovora))}
                    </span>
                  )}
                </td>
                <td style={desno}>
                  <div>{eur(r.ugovorenaVrednost)}</div>
                  <div style={sitno}>{kg(r.ugovorenaKolicina)}</div>
                </td>
                <td style={desno}>
                  <div>{eur(r.isporucenaVrednost)}</div>
                  <div style={sitno}>{kg(r.isporucenaKolicina)}</div>
                </td>
                <td style={desno}>
                  <div>{eur(r.preostalaVrednost)}</div>
                  <div style={sitno}>{kg(r.preostalaKolicina)}</div>
                </td>
                <td style={desno}>
                  {r.realizacijaPct == null ? "—" : (
                    <>
                      <div>{formatNumber(r.realizacijaPct, locale, { maximumFractionDigits: 0 })}%</div>
                      {/* Boja se meri protiv proteklog roka, ne protiv fiksnog praga: 40% u
                          januaru je dobro, a u decembru nije. */}
                      <div style={traka}>
                        <div style={{
                          ...trakaPuna,
                          width: `${Math.min(100, Math.max(0, r.realizacijaPct))}%`,
                          background: r.protekloPct == null ? "var(--ink-4)"
                            : r.realizacijaPct - r.protekloPct < -5 ? "var(--err)"
                            : r.realizacijaPct - r.protekloPct > 5 ? "var(--ok)" : "var(--warn)",
                        }} />
                        {/* Oznaka dokle je rok stigao. Dva broja jedan ispod drugog traže od čoveka
                            da ih oduzme; crta na traci pokazuje isto bez računanja. */}
                        {r.protekloPct != null && (
                          <span style={{ ...oznakaRoka, left: `${Math.min(100, Math.max(0, r.protekloPct))}%` }} />
                        )}
                      </div>
                      {r.protekloPct != null && (
                        <div style={sitno}
                             title={t("contracts.overview.elapsedHelp",
                                      "Koliko je ugovorenog roka prošlo. Realizacija ispod ovog broja znači zaostajanje.")}>
                          {t("contracts.overview.elapsedShort", "prošlo {{n}}% roka")
                            .replace("{{n}}", formatNumber(r.protekloPct, locale, { maximumFractionDigits: 0 }))}
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const okvir: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)",
  padding: 16, display: "flex", flexDirection: "column", gap: 12, marginBottom: 16,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
};
const tiho: CSSProperties = { fontSize: 12.5, color: "var(--ink-3)" };
const sitno: CSSProperties = { fontSize: 11.5, color: "var(--ink-4)" };
const desno: CSSProperties = { textAlign: "right", fontVariantNumeric: "tabular-nums" };
const podnaslov: CSSProperties = {
  margin: "4px 0 8px", fontSize: 14, color: "var(--ink)", fontWeight: 600,
};
const alatke: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10,
};
const cip = (izabran: boolean): CSSProperties => ({
  padding: "4px 11px", fontSize: 12, borderRadius: 999, cursor: "pointer",
  border: `1px solid ${izabran ? "var(--brand-primary)" : "var(--border)"}`,
  background: izabran ? "var(--brand-primary)" : "transparent",
  color: izabran ? "var(--on-primary)" : "var(--ink-2)",
});
const naziv: CSSProperties = {
  maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const kutija: CSSProperties = {
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", padding: 12,
  minWidth: 0, overflowX: "auto",
};
const traka: CSSProperties = {
  position: "relative", height: 4, background: "var(--hover)", borderRadius: 999, marginTop: 3,
};
const oznakaRoka: CSSProperties = {
  position: "absolute", top: -2, width: 2, height: 8, background: "var(--ink-3)",
  borderRadius: 1, transform: "translateX(-1px)",
};
const trakaPuna: CSSProperties = { height: "100%", borderRadius: 999 };
const tempoOkvir: CSSProperties = {
  display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
  border: "1px solid var(--border)", borderRadius: "var(--r-md, 10px)", padding: "10px 12px",
};
